import React, { useContext, useEffect, useState } from 'react';

import clsx from 'clsx';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import {
  FiAlertCircle,
  FiCheck,
  FiCreditCard,
  FiLock,
  FiMapPin,
  FiMaximize2,
  FiSend,
  FiSmartphone,
  FiUser,
  FiUserPlus,
  FiUsers,
  FiX,
} from 'react-icons/fi';

import { createWorkerInvitesRequest } from '../../../../api/invites';
import { ChatCard, ChatCardSection } from '../../../../components/Chat/kit';
import { LailaCardsContext } from './LailaCardsContext';
import { WORKER_INVITE_FORM, useOpenPage, workerInvitePrefillFrom } from './fullForm';
import { workerInvitePrefill } from '../../../invites/prefill';
import type { LailaCardViewProps } from './LailaCard';
import {
  InviteChoice,
  InviteDraftChange,
  InviteDraftData,
  InviteGroup,
  InviteModule,
  InviteTarget,
  MODULE_ANSWERS,
  NewPerson,
  PAY_SCHEMES,
  STEP_NUMBER,
  applyInviteChange,
  groupLeftOut,
  isGuided,
  isOnVyce,
  personAnswer,
  personKey,
  displayPhone,
  inviteCount,
  inviteErrorMessage,
  invitePayload,
  inviteProblems,
  inviteWhere,
  people,
} from './inviteDraft';

type TargetKey = 'team' | 'location' | 'shift' | 'paySchedule';

const MODULES = {
  team: { label: 'Team', module: 'Teams', question: 'Which team?', icon: <FiUsers aria-hidden /> },
  location: {
    label: 'Time & Attendance',
    module: 'Time & Attendance',
    question: 'Which site?',
    icon: <FiMapPin aria-hidden />,
  },
  paySchedule: {
    label: 'Payroll',
    module: 'Payroll',
    question: 'Which pay schedule?',
    icon: <FiCreditCard aria-hidden />,
  },
};

const choiceText = (choice: InviteChoice) =>
  choice.detail ? `${choice.name} · ${choice.detail}` : choice.name;

// An invite Laila drafted (the invite_draft card): the user checks it, picks
// what is left open and sends it from here. Sending is this page's own call
// to the Vyce API; Laila only hears that it happened.
export const InviteDraftCard: React.FC<LailaCardViewProps> = ({ card }) => {
  const classes = useStyles();
  const { updateCard, noteForLaila } = useContext(LailaCardsContext);
  const [open, setOpen] = useState<TargetKey | null>(null);
  const data = card.data as unknown as InviteDraftData;
  const count = inviteCount(data);
  const where = inviteWhere(data);

  const save = (next: InviteDraftData) => updateCard(card.id, next as unknown as Record<string, unknown>);
  const change = (next: InviteDraftChange) => {
    save(applyInviteChange(data, next));
    setOpen(null);
  };

  const send = async () => {
    if (inviteProblems(data).length || data.state === 'sending') {
      return;
    }
    save({ ...data, state: 'sending', message: undefined });
    try {
      const res = await createWorkerInvitesRequest({
        companyId: data.companyId,
        payload: invitePayload(data),
      });
      save({ ...data, state: 'started', message: res.data?.message });
      noteForLaila(`The user sent the invite from the card: ${people(count)} to ${where}.`);
    } catch (e) {
      save({ ...data, state: 'failed', message: inviteErrorMessage(e) });
    }
  };

  const cancel = () => {
    save({ ...data, state: 'cancelled' });
    noteForLaila('The user cancelled the invite draft, so nothing was sent.');
  };

  const openPage = useOpenPage();
  const openFullForm = () => {
    workerInvitePrefill.put(workerInvitePrefillFrom(data));
    noteForLaila('The user took the invite draft to the full invite form, to finish it there.');
    openPage?.(WORKER_INVITE_FORM);
  };

  if (isGuided(data)) {
    return (
      <GuidedInvite card={card} data={data} onCancel={cancel} onOpenFullForm={openPage && openFullForm} />
    );
  }

  if (data.state === 'cancelled') {
    return (
      <ChatCard
        label="Invite draft"
        icon={<FiX aria-hidden />}
        iconTone="neutral"
        title="Invite cancelled"
        subtitle={`${people(count)} · nothing was sent`}
        footer={
          <Button color="primary" size="small" onClick={() => save({ ...data, state: undefined })}>
            Restore
          </Button>
        }
      />
    );
  }

  if (data.state === 'sending' || data.state === 'started') {
    const started = data.state === 'started';
    return (
      <ChatCard
        label="Invite"
        icon={started ? <FiCheck aria-hidden /> : <FiSend aria-hidden />}
        iconTone={started ? 'success' : 'brand'}
        title={started ? `${people(count)} invited` : `Inviting ${people(count)}`}
        badge={started ? 'Started' : 'Sending'}
        badgeTone={started ? 'success' : 'neutral'}
      >
        <ChatCardSection>
          <Typography variant="body2">
            {[
              groupSummary(data),
              ...data.people.map(p => (isOnVyce(p) ? p.name : p.valid ? displayPhone(p.phone) : '')),
            ]
              .filter(Boolean)
              .join(', ')}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {where}
          </Typography>
        </ChatCardSection>
        <ChatCardSection>
          <Typography variant="caption" color="textSecondary">
            {started
              ? data.message || 'The invites are on their way. A summary will land in your Vyce messages.'
              : 'Starting the invites…'}
          </Typography>
        </ChatCardSection>
      </ChatCard>
    );
  }

  const problems = inviteProblems(data);
  const failed = data.state === 'failed';

  const targetRow = (key: 'team' | 'location' | 'paySchedule', target: InviteTarget) => {
    const module = MODULES[key];
    if (target.unavailable) {
      return (
        <Box className={classes.row} key={key}>
          <Box className={classes.rowIcon}>
            <FiLock aria-hidden />
          </Box>
          <Box className={classes.grow}>
            <Typography variant="caption" color="textSecondary">
              {module.label}
            </Typography>
            <Typography variant="body2">You can’t add people to {module.module}</Typography>
          </Box>
          <Button color="primary" size="small" onClick={() => change({ type: 'drop', target: key })}>
            Leave out
          </Button>
        </Box>
      );
    }
    return (
      <Box key={key}>
        <Choice
          label={module.label}
          icon={module.icon}
          question={module.question}
          target={target}
          open={open === key}
          onOpen={() => setOpen(open === key ? null : key)}
          onPick={id => change({ type: 'pick', target: key, id })}
        />
        {key === 'location' && data.location?.chosen && (
          <Box className={classes.nested}>
            {data.location.shift && data.location.shift.options.length ? (
              <Choice
                label="Shift"
                question={`Which shift at ${data.location.chosen.name}?`}
                target={data.location.shift}
                open={open === 'shift'}
                onOpen={() => setOpen(open === 'shift' ? null : 'shift')}
                onPick={id => change({ type: 'pick', target: 'shift', id })}
              />
            ) : (
              <Typography variant="caption" color="textSecondary">
                {data.location.chosen.name} has no shifts yet. Add one in Time &amp; Attendance first.
              </Typography>
            )}
          </Box>
        )}
        {key === 'paySchedule' && data.paySchedule?.chosen && (
          <Box className={classes.nested}>
            <Typography variant="caption" color="textSecondary" component="p">
              Engaged as
            </Typography>
            <Box className={classes.chips} role="group" aria-label="Engaged as">
              {PAY_SCHEMES.map(scheme => (
                <Chip
                  key={scheme.value}
                  label={scheme.label}
                  clickable
                  color={data.paySchedule?.scheme === scheme.value ? 'primary' : 'default'}
                  variant={data.paySchedule?.scheme === scheme.value ? 'default' : 'outlined'}
                  aria-pressed={data.paySchedule?.scheme === scheme.value}
                  onClick={() => change({ type: 'scheme', scheme: scheme.value })}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <ChatCard
      label="Invite draft"
      icon={<FiUserPlus aria-hidden />}
      title={`Invite ${people(count)}`}
      badge={failed ? 'Not sent' : problems.length ? `${problems.length} to fix` : 'Draft'}
      badgeTone={failed ? 'error' : problems.length ? 'warning' : 'brand'}
      footer={
        <Box className={classes.footer}>
          <Box className={classes.buttons}>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              startIcon={<FiSend aria-hidden />}
              disabled={!!problems.length}
              onClick={send}
            >
              {failed ? 'Try again' : `Invite ${people(count)}`}
            </Button>
            <Button onClick={cancel}>Cancel</Button>
            {openPage && (
              <Button color="primary" startIcon={<FiMaximize2 aria-hidden />} onClick={openFullForm}>
                Open full form
              </Button>
            )}
          </Box>
          <Typography variant="caption" color="textSecondary">
            {problems[0] || 'Nothing is sent until you tap Invite. New people get a text from Vyce.'}
          </Typography>
        </Box>
      }
    >
      {failed && (
        <ChatCardSection>
          <Box className={classes.alert} role="alert">
            <FiAlertCircle aria-hidden />
            <Typography variant="body2">{data.message}</Typography>
          </Box>
        </ChatCardSection>
      )}
      <ChatCardSection title={`Who · ${count}`}>
        {data.group?.chosen && <GroupPeople data={data} onChange={change} />}
        {data.people.map(person => (
          <PersonRow
            key={personKey(person)}
            person={person}
            onRemove={() => change({ type: 'removePerson', key: personKey(person) })}
          />
        ))}
      </ChatCardSection>
      <ChatCardSection title="Where">
        {data.team && targetRow('team', data.team)}
        {data.location && targetRow('location', data.location)}
        {data.paySchedule && targetRow('paySchedule', data.paySchedule)}
      </ChatCardSection>
    </ChatCard>
  );
};

// One person on the draft: someone new by number, or someone already on Vyce.
const PersonRow: React.FC<{ person: InviteDraftData['people'][number]; onRemove?: () => void }> = ({
  person,
  onRemove,
}) => {
  const classes = useStyles();
  const vyce = isOnVyce(person);
  const label = vyce ? person.name : displayPhone((person as NewPerson).phone);
  const valid = vyce || (person as NewPerson).valid;
  return (
    <Box className={classes.row}>
      <Box className={classes.rowIcon}>{vyce ? <FiUser aria-hidden /> : <FiSmartphone aria-hidden />}</Box>
      <Box className={classes.grow}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="caption" color={valid ? 'textSecondary' : 'error'}>
          {vyce
            ? ['On Vyce · added straight away', person.detail].filter(Boolean).join(' · ')
            : valid
            ? 'Gets a text from Vyce'
            : 'Not a mobile number'}
        </Typography>
      </Box>
      {onRemove && (
        <IconButton size="small" aria-label={`Remove ${label}`} onClick={onRemove}>
          <FiX />
        </IconButton>
      )}
    </Box>
  );
};

const GROUP_NOUNS: Record<InviteModule, string> = {
  team: 'team',
  site: 'site',
  paySchedule: 'pay schedule',
};

// "Everyone in Bricklayers" or "3 from Riverside Wharf", for summaries.
const groupSummary = (data: InviteDraftData) => {
  const group = data.group;
  if (!group?.chosen) {
    return '';
  }
  return group.everyone
    ? `Everyone in ${group.chosen.name}`
    : `${data.local?.picked?.length ?? 0} from ${group.chosen.name}`;
};

// A list this long gets a search box.
const SEARCH_FROM = 8;

// People from a team, site or pay schedule. Everyone: one row, and the list to
// leave people out. Some: the list to tick who goes.
const GroupPeople: React.FC<{ data: InviteDraftData; onChange: (change: InviteDraftChange) => void }> = ({
  data,
  onChange,
}) => {
  const classes = useStyles();
  const group = data.group as InviteGroup;
  const name = group.chosen!.name;
  const members = group.members ?? [];
  const [open, setOpen] = useState(!group.everyone);
  const [search, setSearch] = useState('');
  const leftOut = groupLeftOut(data);
  const except = (group.except ?? []).map(person => person.userId);
  const picked = data.local?.picked ?? [];
  const ticked = (id: string) => (group.everyone ? !leftOut.includes(id) : picked.includes(id));
  const shown = members.filter(member => member.name.toLowerCase().includes(search.trim().toLowerCase()));
  const allTicked = members.length > 0 && members.every(member => ticked(member.id));

  return (
    <Box>
      <Box className={classes.row}>
        <Box className={classes.rowIcon}>
          <FiUsers aria-hidden />
        </Box>
        <Box className={classes.grow}>
          <Typography variant="body2">{group.everyone ? `Everyone in ${name}` : `From ${name}`}</Typography>
          <Typography variant="caption" color="textSecondary">
            {group.everyone
              ? [
                  `${group.count ?? 0} people`,
                  group.except?.length ? `not ${group.except.map(person => person.name).join(', ')}` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')
              : `${picked.length} of ${group.count ?? members.length} ticked`}
          </Typography>
        </Box>
        {group.everyone && members.length > 0 && (
          <Button color="primary" size="small" onClick={() => setOpen(value => !value)} aria-expanded={open}>
            {open ? 'Hide' : `See all ${group.count ?? members.length}`}
          </Button>
        )}
      </Box>
      {group.membersUnavailable && (
        <Typography variant="caption" color="textSecondary" component="p" className={classes.nested}>
          You can’t see who’s in {name}. Open the full form to pick them.
        </Typography>
      )}
      {open && members.length > 0 && (
        <Box className={classes.nested}>
          <Box className={classes.listTools}>
            {members.length >= SEARCH_FROM && (
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search by name"
                inputProps={{ 'aria-label': `Search ${name}` }}
                value={search}
                onChange={event => setSearch(event.target.value)}
                className={classes.grow}
              />
            )}
            <Button
              color="primary"
              size="small"
              onClick={() => onChange({ type: 'tickAll', on: !allTicked })}
            >
              {allTicked ? 'Untick all' : `Select all ${members.length}`}
            </Button>
          </Box>
          <Box
            role="group"
            aria-label={group.everyone ? `Who goes from ${name}` : `Who to add from ${name}`}
          >
            {shown.map(member => (
              <FormControlLabel
                key={member.id}
                className={classes.member}
                control={
                  <Checkbox
                    color="primary"
                    size="small"
                    checked={ticked(member.id)}
                    disabled={group.everyone && except.includes(member.id)}
                    onChange={() => onChange({ type: 'tick', id: member.id })}
                  />
                }
                label={
                  <Box className={classes.grow}>
                    <Typography variant="body2">{member.name}</Typography>
                    {member.detail && (
                      <Typography variant="caption" color="textSecondary">
                        {member.detail}
                      </Typography>
                    )}
                  </Box>
                }
              />
            ))}
          </Box>
          {(group.count ?? 0) > members.length && (
            <Typography variant="caption" color="textSecondary">
              Showing {members.length} of {group.count}. Open the full form for the rest.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

const MODULE_LABELS: Record<InviteModule, string> = {
  team: 'Team',
  site: 'Site & shift',
  paySchedule: 'Pay schedule',
};

// What one of Laila's questions offers to tap.
interface Ask {
  question: string;
  options: { key: string; label: string; answer: string }[];
}

const askFor = (data: InviteDraftData): Ask | null => {
  const choices = (items: InviteChoice[], answer = (item: InviteChoice) => item.name) =>
    items.map(item => ({ key: item.id, label: choiceText(item), answer: answer(item) }));
  switch (data.step) {
    case 'group': {
      const group = data.group;
      return group
        ? {
            question: `Which ${GROUP_NOUNS[group.kind]} are they from?`,
            options: choices(group.options),
          }
        : null;
    }
    case 'which': {
      const person = (data.unresolved || []).find(item => item.options.length);
      return person
        ? {
            question: `Which ${person.asked}?`,
            options: [
              ...choices(person.options, item => personAnswer(item, person.options)),
              { key: 'new', label: 'Someone new', answer: `Someone new, not ${person.asked}` },
            ],
          }
        : null;
    }
    case 'module':
      return {
        question: 'Where should they go?',
        options: (data.offer || []).map(module => ({
          key: module,
          label: MODULE_LABELS[module],
          answer: MODULE_ANSWERS[module],
        })),
      };
    case 'team':
      return data.team ? { question: 'Which team?', options: choices(data.team.options) } : null;
    case 'site':
      return data.location ? { question: 'Which site?', options: choices(data.location.options) } : null;
    case 'shift':
      return data.location?.shift
        ? {
            question: `Which shift at ${data.location.chosen?.name}?`,
            options: choices(data.location.shift.options, item => `The ${item.name} shift`),
          }
        : null;
    case 'pay_schedule':
      return data.paySchedule
        ? { question: 'Which pay schedule?', options: choices(data.paySchedule.options) }
        : null;
    case 'scheme':
      return {
        question: 'How are they engaged?',
        options: PAY_SCHEMES.map(scheme => ({
          key: scheme.value,
          label: scheme.label,
          answer: scheme.label,
        })),
      };
    default:
      return null;
  }
};

// Where they're going so far: "Bricklayers · Riverside Wharf".
const placesSoFar = (data: InviteDraftData) =>
  [
    data.team && (data.team.chosen?.name || 'Team'),
    data.location && (data.location.chosen?.name || 'Site'),
    data.paySchedule && (data.paySchedule.chosen?.name || 'Pay schedule'),
  ]
    .filter(Boolean)
    .join(' · ');

interface GuidedProps {
  card: LailaCardViewProps['card'];
  data: InviteDraftData;
  onCancel: () => void;
  onOpenFullForm?: () => void;
}

// The invite while Laila asks for it, one step at a time: who, what kind of
// place, which one. Each answer can be said, typed or tapped; a tap goes to
// Laila as the user's answer, and she sends the card back filled in.
const GuidedInvite: React.FC<GuidedProps> = ({ card, data, onCancel, onOpenFullForm }) => {
  const classes = useStyles();
  const { answer } = useContext(LailaCardsContext);
  const [phone, setPhone] = useState('');
  // One answer per question: taps wait for Laila's next one.
  const [answered, setAnswered] = useState(false);
  useEffect(() => setAnswered(false), [data.step, data.people.length]);

  const step = STEP_NUMBER[data.step as keyof typeof STEP_NUMBER] ?? 1;
  const ask = askFor(data);
  const say = (text: string) => {
    if (!answer || answered || !text.trim()) {
      return;
    }
    setAnswered(true);
    answer(text.trim());
  };
  const notFound = (data.unresolved || []).filter(item => !item.options.length).map(item => item.asked);

  const stepTitle = (n: number, title: string, summary?: string) => (
    <Box className={classes.stepHead}>
      <span
        className={clsx(classes.stepDot, {
          [classes.stepDone]: n < step,
          [classes.stepOn]: n === step,
        })}
        aria-hidden
      >
        {n < step ? <FiCheck /> : n}
      </span>
      <Box className={classes.grow}>
        <Typography variant="body2" className={n === step ? classes.stepTitleOn : undefined}>
          {title}
        </Typography>
        {summary && (
          <Typography variant="caption" color="textSecondary">
            {summary}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const chips = ask && (
    <Box className={classes.chips} role="group" aria-label={ask.question}>
      {ask.options.map(option => (
        <Chip
          key={option.key}
          label={option.label}
          clickable
          variant="outlined"
          disabled={!answer || answered}
          onClick={() => say(option.answer)}
        />
      ))}
    </Box>
  );

  return (
    <ChatCard
      label="Invite"
      icon={<FiUserPlus aria-hidden />}
      title="Invite a worker"
      badge={`Step ${step} of 3`}
      badgeTone="brand"
      footer={
        <Box className={classes.buttons}>
          {onOpenFullForm && (
            <Button color="primary" startIcon={<FiMaximize2 aria-hidden />} onClick={onOpenFullForm}>
              Open full form
            </Button>
          )}
          <Button onClick={onCancel}>Cancel</Button>
        </Box>
      }
    >
      <ChatCardSection>
        {stepTitle(
          1,
          'Who',
          step > 1
            ? [
                groupSummary(data),
                ...data.people.map(p => (isOnVyce(p) ? p.name : displayPhone((p as NewPerson).phone))),
              ]
                .filter(Boolean)
                .join(', ')
            : undefined
        )}
        {step === 1 && (
          <Box className={classes.nested}>
            {data.step === 'which' || data.step === 'group' ? (
              <>
                <Typography variant="caption" color="textSecondary" component="p">
                  {ask?.question}
                </Typography>
                {chips}
              </>
            ) : (
              <form
                className={classes.phoneForm}
                onSubmit={event => {
                  event.preventDefault();
                  say(phone);
                }}
              >
                <TextField
                  id={`${card.id}-phone`}
                  label="Mobile number"
                  size="small"
                  variant="outlined"
                  inputProps={{ inputMode: 'tel' }}
                  placeholder="07700 900123"
                  value={phone}
                  onChange={event => setPhone(event.target.value)}
                  disabled={!answer || answered}
                  className={classes.grow}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disableElevation
                  disabled={!answer || answered || !phone.trim()}
                >
                  Add
                </Button>
              </form>
            )}
            <Typography variant="caption" color="textSecondary">
              {notFound.length
                ? `No one called ${notFound.join(' or ')} in your company. Add their mobile to invite them.`
                : 'Or say the name of someone already on Vyce.'}
            </Typography>
          </Box>
        )}
      </ChatCardSection>
      <ChatCardSection>
        {stepTitle(2, 'Module', step > 2 ? placesSoFar(data) : undefined)}
        {step === 2 && (
          <Box className={classes.nested}>
            {ask?.options.length ? (
              chips
            ) : (
              <Typography variant="caption" color="textSecondary">
                You can’t add people to any team, site or pay schedule here.
              </Typography>
            )}
          </Box>
        )}
      </ChatCardSection>
      <ChatCardSection>
        {stepTitle(3, step === 3 && ask ? ask.question : 'Where exactly')}
        {step === 3 && <Box className={classes.nested}>{chips}</Box>}
      </ChatCardSection>
    </ChatCard>
  );
};

interface ChoiceProps {
  label: string;
  icon?: React.ReactNode;
  question: string;
  target: InviteTarget;
  open: boolean;
  onOpen: () => void;
  onPick: (id: string) => void;
}

// One target: what it is set to, or the options to pick from.
const Choice: React.FC<ChoiceProps> = ({ label, icon, question, target, open, onOpen, onPick }) => {
  const classes = useStyles();
  const showOptions = !target.chosen || open;

  return (
    <Box>
      <Box className={classes.row}>
        {icon && <Box className={classes.rowIcon}>{icon}</Box>}
        <Box className={classes.grow}>
          <Typography variant="caption" color="textSecondary">
            {label}
          </Typography>
          <Typography variant="body2">
            {target.chosen
              ? choiceText(target.chosen)
              : target.asked
              ? `Not sure which “${target.asked}”`
              : question}
          </Typography>
        </Box>
        {target.chosen && target.options.length > 1 && (
          <Button color="primary" size="small" onClick={onOpen} aria-expanded={open}>
            {open ? 'Done' : 'Change'}
          </Button>
        )}
      </Box>
      {showOptions && (
        <Box className={classes.chips} role="group" aria-label={question}>
          {target.options.map(option => (
            <Chip
              key={option.id}
              label={choiceText(option)}
              clickable
              color={target.chosen?.id === option.id ? 'primary' : 'default'}
              variant={target.chosen?.id === option.id ? 'default' : 'outlined'}
              aria-pressed={target.chosen?.id === option.id}
              onClick={() => onPick(option.id)}
            />
          ))}
          {!target.options.length && (
            <Typography variant="caption" color="textSecondary">
              Nothing to pick here yet.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    row: { display: 'flex', alignItems: 'center', gap: 12, minHeight: 44 },
    rowIcon: {
      flex: 'none',
      width: 34,
      height: 34,
      borderRadius: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 17,
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.action.hover,
    },
    grow: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
    nested: { paddingLeft: 46, paddingBottom: 6 },
    listTools: { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' },
    member: { display: 'flex', margin: 0, width: '100%', alignItems: 'center' },
    stepHead: { display: 'flex', alignItems: 'center', gap: 12, minHeight: 44 },
    stepDot: {
      width: 26,
      height: 26,
      flex: 'none',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      border: `1px solid ${theme.palette.divider}`,
      boxSizing: 'border-box',
    },
    stepOn: {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
    },
    stepDone: {
      color: theme.palette.success.main,
      borderColor: theme.palette.success.main,
    },
    stepTitleOn: { fontWeight: 600 },
    phoneForm: { display: 'flex', gap: 8, alignItems: 'center', padding: '8px 0 6px' },
    chips: { display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 0 8px' },
    footer: { display: 'flex', flexDirection: 'column', gap: 6, width: '100%' },
    buttons: { display: 'flex', flexWrap: 'wrap', gap: 8 },
    alert: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      color: theme.palette.error.main,
      '& svg': { flex: 'none', marginTop: 3 },
    },
  })
);
